import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, Home, ChevronRight, Phone, AlertTriangle, Brain, Target,
  ClipboardList, Sun, Sunset, Moon, Heart, ShieldAlert, Siren, FileText, User,
  AlertOctagon, CalendarClock, Lock,
} from 'lucide-react';
import { secureStore, TTL } from '@/lib/offline/secureStore';
import { MobileBottomNav } from '@/components/mobile/MobileNavigation';
import MobileAccessDenied from '@/components/mobile/MobileAccessDenied';

function Section({ title, icon: Icon, children, testid, tone = 'default' }) {
  const tones = { default: 'text-primary', danger: 'text-red-500', warning: 'text-amber-500', success: 'text-emerald-500' };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`h-4 w-4 ${tones[tone]}`} />
          <p className="text-sm font-semibold">{title}</p>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <p className="text-sm"><span className="text-muted-foreground">{label}: </span>{value}</p>
  );
}

export default function MobileCarePlanBriefPage() {
  const navigate = useNavigate();
  const { participantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [b, setB] = useState(null);

  const cacheKey = `care_plan_brief:${participantId}`;

  const load = useCallback(async () => {
    setLoading(true); setForbidden(false); setNotFound(false);
    try {
      const res = await api.get(`/mobile/participants/${participantId}/care-plan-brief`);
      const data = res.data?.data || res.data;
      setB(data); setFromCache(false);
      try { await secureStore.setItem(cacheKey, data, TTL.SHIFT_META); } catch (_) { /* noop */ }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) setForbidden(true);
      else if (status === 404) setNotFound(true);
      else {
        let snap = null;
        try { snap = await secureStore.getItem(cacheKey); } catch (_) { /* noop */ }
        if (snap) { setB(snap); setFromCache(true); toast.info('Showing saved offline copy'); }
        else toast.error('Could not load care plan brief. Check your connection.');
      }
    } finally {
      setLoading(false);
    }
  }, [participantId, cacheKey]);

  useEffect(() => { load(); }, [load]);

  if (forbidden) {
    return (
      <MobileAccessDenied
        title="Participant not assigned"
        message="You can only view care plans for participants assigned to your shifts. Contact your coordinator if you believe this is a mistake."
        onBack={() => navigate(-1)}
      />
    );
  }
  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center" data-testid="brief-not-found">
        <User className="h-12 w-12 text-muted-foreground mb-4" />
        <h1 className="text-lg font-semibold mb-2">Participant not found</h1>
        <Button onClick={() => navigate(-1)} className="gap-2"><ArrowLeft className="h-4 w-4" /> Go back</Button>
      </div>
    );
  }

  const p = b?.participant;
  const initials = (p?.name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const sn = b?.supportNeeds || {};
  const dr = b?.dailyRoutine || {};
  const risks = b?.risks || {};
  const contacts = b?.escalation?.emergencyContacts || [];
  const riskFlags = b?.riskFlags || [];
  const review = b?.review || {};
  const perms = b?.permissions || {};
  const hasSupportNeeds = sn.personalCare || sn.mobility || sn.communication || sn.communityAccess;
  const hasRoutine = dr.morning || dr.afternoon || dr.evening || dr.night;
  const hasRisks = risks.allergies || risks.medicalAlerts || risks.dietaryRequirements;

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-care-plan-brief">
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="-ml-2" onClick={() => navigate(-1)} data-testid="brief-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <button onClick={() => navigate('/mobile')} className="flex items-center gap-1 hover:text-foreground">
                <Home className="h-3 w-3" /> Home
              </button>
              <ChevronRight className="h-3 w-3" />
              {participantId && (
                <>
                  <button onClick={() => navigate(`/mobile/participants/${participantId}`)} className="hover:text-foreground">Participant</button>
                  <ChevronRight className="h-3 w-3" />
                </>
              )}
              <span>Care Plan</span>
            </div>
            <h1 className="text-base font-bold truncate">Care Plan Brief</h1>
          </div>
          {perms.readOnly !== false && (
            <Badge variant="secondary" className="text-[10px] gap-1" data-testid="brief-readonly-badge">
              <Lock className="h-3 w-3" /> Read-only
            </Badge>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-4 space-y-4" data-testid="brief-skeleton">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      ) : !b ? (
        <div className="p-6 text-center text-muted-foreground">Unable to load care plan brief.</div>
      ) : (
        <div className="p-4 space-y-4">
          {fromCache && (
            <div className="flex items-center gap-2 rounded-xl bg-blue-500/10 border border-blue-500/20 px-4 py-3 text-blue-700 text-sm" data-testid="brief-offline-banner">
              <ShieldAlert className="h-4 w-4" /> Offline — showing a saved copy of this care plan.
            </div>
          )}

          {/* Header */}
          <Card data-testid="brief-header">
            <CardContent className="p-4 flex items-center gap-4">
              {p?.photoUrl ? (
                <img src={p.photoUrl} alt={p.name} className="h-14 w-14 rounded-full object-cover" />
              ) : (
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">{initials}</div>
              )}
              <div className="min-w-0">
                <p className="font-bold text-lg truncate">{p?.preferredName || p?.name}</p>
                {b.title && <p className="text-xs text-muted-foreground truncate">{b.title}</p>}
                {review.lastReviewedDate && (
                  <p className="text-[11px] text-muted-foreground">Last reviewed: {String(review.lastReviewedDate).slice(0, 10)}</p>
                )}
                {review.nextReviewDate && (
                  <div className="flex items-center gap-1 mt-1" data-testid="brief-review-chip">
                    <CalendarClock className="h-3 w-3" />
                    {review.status === 'OVERDUE' ? (
                      <Badge className="bg-red-600 text-white text-[10px]">Review overdue · {String(review.nextReviewDate).slice(0, 10)}</Badge>
                    ) : review.status === 'DUE_SOON' ? (
                      <Badge className="bg-amber-500 text-white text-[10px]">Review due soon · {String(review.nextReviewDate).slice(0, 10)}</Badge>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">Next review: {String(review.nextReviewDate).slice(0, 10)}</span>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {!b.hasCarePlan && (
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 px-4 py-3 text-amber-700 text-sm" data-testid="brief-no-plan">
              No approved care plan is available for this participant yet. Check the participant profile or contact your coordinator.
            </div>
          )}

          {/* High-risk / critical alerts banner (Day 22) */}
          {riskFlags.length > 0 && (
            <Card className="border-red-300 bg-red-50/60" data-testid="brief-risk-flags">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertOctagon className="h-4 w-4 text-red-600" />
                  <p className="text-sm font-semibold text-red-700">Critical / high-risk alerts</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {riskFlags.map((f, i) => (
                    <Badge
                      key={i}
                      data-testid="brief-risk-flag-item"
                      className={`text-xs ${f.severity === 'CRITICAL' ? 'bg-red-600' : 'bg-amber-500'} text-white`}
                    >
                      {f.severity === 'CRITICAL' ? '⚠ ' : ''}{f.label}
                    </Badge>
                  ))}
                </div>
                <p className="text-[11px] text-red-700/80 mt-2">Read these instructions in full below before starting support.</p>
              </CardContent>
            </Card>
          )}

          {/* Risks first — safety critical */}
          <Section title="Risks & Medical Alerts" icon={AlertTriangle} testid="brief-risks" tone="danger">
            {!hasRisks ? (
              <p className="text-xs text-muted-foreground">None recorded.</p>
            ) : (
              <div className="space-y-1">
                <Field label="Allergies" value={risks.allergies} />
                <Field label="Medical alerts" value={risks.medicalAlerts} />
                <Field label="Dietary requirements" value={risks.dietaryRequirements} />
              </div>
            )}
          </Section>

          {/* Behaviour support */}
          <Section title="Behaviour Support" icon={Brain} testid="brief-behaviour" tone="warning">
            <p className="text-sm whitespace-pre-wrap">{b.behaviourSupport || 'No behaviour support strategies recorded.'}</p>
          </Section>

          {/* Support needs */}
          <Section title="Support Needs" icon={Heart} testid="brief-support-needs">
            {!hasSupportNeeds ? (
              <p className="text-xs text-muted-foreground">No specific support needs recorded.</p>
            ) : (
              <div className="space-y-1">
                <Field label="Personal care" value={sn.personalCare} />
                <Field label="Mobility" value={sn.mobility} />
                <Field label="Communication" value={sn.communication} />
                <Field label="Community access" value={sn.communityAccess} />
              </div>
            )}
          </Section>

          {/* Daily routine */}
          <Section title="Daily Routine" icon={ClipboardList} testid="brief-routine">
            {!hasRoutine ? (
              <p className="text-xs text-muted-foreground">No routine recorded.</p>
            ) : (
              <div className="space-y-2 text-sm">
                {dr.morning && <p className="flex gap-2"><Sun className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" /><span><span className="text-muted-foreground">Morning: </span>{dr.morning}</span></p>}
                {dr.afternoon && <p className="flex gap-2"><Sun className="h-4 w-4 text-orange-500 shrink-0 mt-0.5" /><span><span className="text-muted-foreground">Afternoon: </span>{dr.afternoon}</span></p>}
                {dr.evening && <p className="flex gap-2"><Sunset className="h-4 w-4 text-violet-500 shrink-0 mt-0.5" /><span><span className="text-muted-foreground">Evening: </span>{dr.evening}</span></p>}
                {dr.night && <p className="flex gap-2"><Moon className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" /><span><span className="text-muted-foreground">Night: </span>{dr.night}</span></p>}
              </div>
            )}
          </Section>

          {/* BS3-012: plans built from structured templates (their content is not in the flat fields above) */}
          {(b.structuredSections || []).map((s) => (
            <Section key={s.key} title={s.title} icon={ClipboardList} testid={`brief-structured-${s.key}`}>
              <div className="space-y-2">
                {s.rows.map((r, i) => (
                  <div key={i} className="text-sm border-b last:border-0 pb-2 last:pb-0" data-testid="brief-structured-row">
                    {r.need && <p className="font-medium">{r.need}</p>}
                    {r.instruction && <p className="whitespace-pre-wrap">{r.instruction}</p>}
                    {(r.supportLevel || r.riskLevel || r.frequency) && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {[r.supportLevel && r.supportLevel.replace(/_/g, ' ').toLowerCase(), r.frequency && r.frequency.replace(/_/g, ' '), r.riskLevel && `${r.riskLevel.toLowerCase()} risk`].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {r.escalation && <p className="text-xs text-amber-700 mt-0.5">Escalate: {r.escalation}</p>}
                  </div>
                ))}
              </div>
            </Section>
          ))}

          {/* Important notes */}
          {b.importantNotes && (
            <Section title="Important Care Notes" icon={FileText} testid="brief-notes" tone="warning">
              <p className="text-sm whitespace-pre-wrap text-amber-700">{b.importantNotes}</p>
            </Section>
          )}

          {/* Goals */}
          <Section title="Goals" icon={Target} testid="brief-goals" tone="success">
            {(b.goals || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No active goals.</p>
            ) : (
              <ul className="space-y-2">
                {b.goals.map((g) => (
                  <li key={g.id} className="text-sm" data-testid="brief-goal-item">
                    <p className="font-medium">{g.title}</p>
                    {g.description && <p className="text-xs text-muted-foreground whitespace-pre-wrap">{g.description}</p>}
                    <p className="text-[11px] text-muted-foreground">{g.category}{g.progress ? ` · ${String(g.progress).replace(/_/g, ' ')}` : ''}</p>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Escalation */}
          <Section title="Escalation & Emergency" icon={Siren} testid="brief-escalation" tone="danger">
            <p className="text-sm mb-2">{b.escalation?.guidance}</p>
            {contacts.length === 0 ? (
              <p className="text-xs text-muted-foreground">No emergency contacts on file.</p>
            ) : (
              <div className="space-y-2">
                {contacts.map((c, i) => (
                  <div key={i} className="flex items-center justify-between" data-testid="brief-contact-item">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.name || c.label}</p>
                      <p className="text-xs text-muted-foreground">{c.label}{c.phone ? ` · ${c.phone}` : ''}</p>
                    </div>
                    {c.phone && (
                      <a href={`tel:${c.phone}`} data-testid="brief-contact-call">
                        <Button size="sm" className="gap-1"><Phone className="h-4 w-4" /> Call</Button>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}

      <MobileBottomNav />
    </div>
  );
}
